import React from 'react';
import { useEnquiryStore } from '../../store/useEnquiryStore';
import * as XLSX from 'xlsx';

export const ReviewForm: React.FC = () => {
  const { currentEnquiry, saveEnquiry } = useEnquiryStore();

  const handleSaveEnquiry = () => {
    saveEnquiry();
    alert('Enquiry saved successfully!');
  };

  const handleGenerateExcel = () => {
    const workbook = XLSX.utils.book_new();
    
    // Customer Details Sheet
    const customerData = [
      ['Customer Details', ''],
      ['Name', currentEnquiry.customerDetails.name],
      ['Address', currentEnquiry.customerDetails.address],
      ['Phone', currentEnquiry.customerDetails.phone],
      ['Mobile', currentEnquiry.customerDetails.mobile],
      ['Email', currentEnquiry.customerDetails.email],
      ['How Heard', currentEnquiry.customerDetails.howHeard],
      ['Reason for Enquiry', currentEnquiry.customerDetails.reasonForEnquiry],
      ['Notes', currentEnquiry.customerDetails.notes],
      [''],
      ['Appointment Details', ''],
      ['Date', currentEnquiry.appointmentDetails.date],
      ['Time', currentEnquiry.appointmentDetails.time],
      ['Samples to Take', currentEnquiry.appointmentDetails.samplesToTake ? 'Yes' : 'No'],
      ['Description', currentEnquiry.appointmentDetails.description],
      [''],
      ['Quote Details', ''],
      ['Date Sent', currentEnquiry.quoteDetails.dateSent],
      ['Method', currentEnquiry.quoteDetails.method],
      ['Follow-up Date', currentEnquiry.quoteDetails.followUpDate],
      ['Staff Member', currentEnquiry.quoteDetails.staffMember],
      ['Comments', currentEnquiry.quoteDetails.comments],
      [''],
      ['Outcome', ''],
      ['Status', currentEnquiry.outcome.status],
      ['Notes', currentEnquiry.outcome.notes],
      ['Flag for Accounts', currentEnquiry.outcome.flagForAccounts ? 'Yes' : 'No'],
    ];

    const customerSheet = XLSX.utils.aoa_to_sheet(customerData);
    XLSX.utils.book_append_sheet(workbook, customerSheet, 'Customer Details');

    // Specifications Sheet
    if (currentEnquiry.specifications.length > 0) {
      const specHeaders = [
        'Item #', 'Product', 'Location', 'Qty', 'Fit', 'Fixing', 
        'Width (mm)', 'Drop (mm)', 'Material', 'Bottom Rail', 
        'Control', 'Stack', 'Comments'
      ];
      
      const specData = [
        specHeaders,
        ...currentEnquiry.specifications.map((spec, index) => [
          index + 1,
          spec.product,
          spec.location,
          spec.quantity,
          spec.fit,
          spec.fixing,
          spec.width,
          spec.drop,
          spec.material,
          spec.bottomRail,
          spec.control,
          spec.stack,
          spec.comments
        ])
      ];

      const specSheet = XLSX.utils.aoa_to_sheet(specData);
      XLSX.utils.book_append_sheet(workbook, specSheet, 'Specifications');
    }

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const filename = `Blinds-Enquiry-${currentEnquiry.customerDetails.name.replace(/\s+/g, '-')}-${timestamp}.xlsx`;

    XLSX.writeFile(workbook, filename);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Review & Export</h2>
        <p className="text-sm text-gray-600">
          Review all enquiry details and export to Excel for back-office processing.
        </p>
      </div>

      <div className="space-y-6">
        {/* Customer Details Summary */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Customer Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Name</p>
              <p className="font-medium">{currentEnquiry.customerDetails.name || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Email</p>
              <p className="font-medium">{currentEnquiry.customerDetails.email || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Phone</p>
              <p className="font-medium">{currentEnquiry.customerDetails.phone || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Mobile</p>
              <p className="font-medium">{currentEnquiry.customerDetails.mobile || 'Not specified'}</p>
            </div>
            <div className="md:col-span-2">
              <p className="text-sm text-gray-600">Address</p>
              <p className="font-medium">{currentEnquiry.customerDetails.address || 'Not specified'}</p>
            </div>
          </div>
        </div>

        {/* Appointment Details Summary */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Appointment Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Date</p>
              <p className="font-medium">{formatDate(currentEnquiry.appointmentDetails.date)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Time</p>
              <p className="font-medium">{currentEnquiry.appointmentDetails.time || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Samples to Take</p>
              <p className="font-medium">{currentEnquiry.appointmentDetails.samplesToTake ? 'Yes' : 'No'}</p>
            </div>
          </div>
        </div>

        {/* Quote Details Summary */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Quote Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Date Sent</p>
              <p className="font-medium">{formatDate(currentEnquiry.quoteDetails.dateSent)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Method</p>
              <p className="font-medium">{currentEnquiry.quoteDetails.method}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Follow-up Date</p>
              <p className="font-medium">{formatDate(currentEnquiry.quoteDetails.followUpDate)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Staff Member</p>
              <p className="font-medium">{currentEnquiry.quoteDetails.staffMember || 'Not specified'}</p>
            </div>
          </div>
        </div>

        {/* Outcome Summary */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Outcome</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <p className={`font-medium ${currentEnquiry.outcome.status === 'Successful' ? 'text-green-600' : 'text-red-600'}`}>
                {currentEnquiry.outcome.status}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Flag for Accounts</p>
              <p className="font-medium">{currentEnquiry.outcome.flagForAccounts ? 'Yes' : 'No'}</p>
            </div>
          </div>
        </div>

        {/* Specifications Summary */}
        {currentEnquiry.specifications.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              Specifications ({currentEnquiry.specifications.length} items)
            </h3>
            <div className="space-y-2">
              {currentEnquiry.specifications.map((spec, index) => (
                <div key={spec.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm font-medium">
                    Item #{index + 1}: {spec.product} - {spec.location}
                  </span>
                  <span className="text-sm text-gray-600">
                    {spec.width}mm × {spec.drop}mm
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Attachments Summary */}
        {currentEnquiry.attachments.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              Attachments ({currentEnquiry.attachments.length} files)
            </h3>
            <div className="space-y-2">
              {currentEnquiry.attachments.map((attachment) => (
                <div key={attachment.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm font-medium">{attachment.name}</span>
                  <span className="text-sm text-gray-600">{attachment.type}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200">
          <button
            onClick={handleSaveEnquiry}
            className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Save Enquiry
          </button>
          <button
            onClick={handleGenerateExcel}
            className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download Excel
          </button>
        </div>
      </div>
    </div>
  );
};